import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search as SearchIcon } from 'lucide-react';
import legacyUapiService from '../services/LegacyUapiService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import { formatDateTime } from '../utils/datetime';
import { apiErrorMessage } from '../utils/apiError';
import { useAccount } from '../contexts/AccountContext';

// Universal API > Custom Code — every snippet, the system it is written for, and the backends it runs on.
//
// A snippet is per-client code around a backend system's integration (V528): before() ahead of the call,
// after() once it answers. It is attached on a backend binding's page (Authentication > hotel > backend),
// and this tab answers the question that page cannot: WHERE does this code run.
//
// THE NAVBAR'S CLIENT ONLY (HIL-154). Custom code belongs to a chain (or one hotel of it, V702): on a chain the list is
// that chain's code — another client's is not shown — and on a hotel, the chain-wide code plus that hotel's own. With
// no chain chosen it is every client's, each marked with whom it belongs to.
const UapiCustomCodeTab = () => {
    const navigate = useNavigate();
    const { selectedAccount, selectedHotel } = useAccount();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await legacyUapiService.listCustomCode({ accountId: selectedAccount || undefined, hotelId: selectedHotel || undefined });
            setRows((res.data || res).customCode || []);
        } catch (e) {
            setError(apiErrorMessage(e, 'Failed to load custom code'));
        } finally {
            setLoading(false);
        }
    }, [selectedAccount, selectedHotel]);

    useEffect(() => { load(); }, [load]);

    // Matches the snippet, its system, and every hotel it is attached to — "what runs on Bolton" is the
    // question people most often arrive with.
    const shown = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r => [r.name, r.description, r.system_name, r.owner_chain_name, r.owner_hotel_name, ...(r.modules || []),
            ...(r.attachments || []).flatMap(a => [a.hotelname, a.label, a.uapi_hotel_id]),
            ...(r.routeAttachments || []).flatMap(a => [a.template_name, a.route_name])]
            .some(v => v && String(v).toLowerCase().includes(q)));
    }, [rows, filter]);

    return (
        <>
            <div className="uapi-section-bar">
                <div>
                    <h2 className="uapi-section-title">Custom code</h2>
                    <p className="lu-flow-note" style={{ margin: '0.25rem 0 0' }}>
                        Per-client code that runs before a backend system is called and after it answers —
                        attached to a hotel's backend on its Authentication page.
                    </p>
                </div>
                <Button variant="secondary" onClick={() => navigate('/universal-api/custom-code/new')}>
                    <Plus className="icon" /> Add custom code
                </Button>
            </div>

            {error && <p className="ppw-error-msg">{error}</p>}

            {loading ? (
                <div className="loading-section"><Spinner size="md" /><p>Loading custom code…</p></div>
            ) : rows.length === 0 ? (
                <div className="otx-table-wrap" style={{ padding: '3rem 2rem', textAlign: 'center' }}>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>{selectedAccount ? 'No custom code for this chain yet.' : 'No custom code yet.'}</p>
                    <p style={{ margin: '0.5rem auto 0', color: '#9ca3af', fontSize: '0.825rem', maxWidth: '58ch' }}>
                        Open a hotel's backend system under Authentication and Access to write code for that
                        client — for example, the file name its Azure container expects.
                    </p>
                </div>
            ) : (
                <>
                    <div className="uapi-cc-filter">
                        <SearchIcon size={14} />
                        <input
                            type="text"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filter by name, system, module or hotel"
                        />
                    </div>
                    <div className="otx-table-wrap">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Belongs to</th>
                                    <th>Backend system</th>
                                    <th>Modules</th>
                                    <th>Status</th>
                                    <th>Attached to</th>
                                    <th>Updated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shown.map(r => (
                                    <tr
                                        key={r.id}
                                        className="clickable-row"
                                        title={`Open ${r.name}`}
                                        onClick={() => navigate(`/universal-api/custom-code/${r.id}`)}
                                    >
                                        <td>
                                            <strong>{r.name}</strong>
                                            {r.description && <div className="uapi-binding-label">{r.description}</div>}
                                        </td>
                                        <td>
                                            {r.owner_chain_name
                                                ? <>{r.owner_chain_name}{r.owner_hotel_name && <div className="uapi-binding-label">{r.owner_hotel_name} only</div>}</>
                                                : <span className="lu-muted">No chain — open it to choose one</span>}
                                        </td>
                                        <td><span className="uapi-system-chip">{r.system_name}</span></td>
                                        <td>
                                            {(r.modules || []).length ? (
                                                <span className="uapi-override-cell">
                                                    {r.modules.map(p => (
                                                        <span key={p} className="uapi-module-chip uapi-module-chip-sm">{p}</span>
                                                    ))}
                                                </span>
                                            ) : <span className="lu-muted">none — runs nowhere</span>}
                                        </td>
                                        <td>
                                            <span className={`uapi-cc-state ${r.active === 'Y' ? 'is-on' : ''}`}>
                                                {r.active === 'Y' ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        {/* Each attachment links to where it runs — a pipeline template's
                                            route (V533) or a binding — the page where it is attached,
                                            ordered and detached. */}
                                        <td onClick={(e) => e.stopPropagation()}>
                                            {(r.attachments || []).length === 0 && (r.routeAttachments || []).length === 0 ? (
                                                <span className="lu-muted">Not attached — runs nowhere</span>
                                            ) : (
                                                <div className="uapi-cc-attachments">
                                                    {(r.routeAttachments || []).map(a => (
                                                        <Link
                                                            key={`route-${a.route_id}`}
                                                            className="uapi-cc-attachment"
                                                            to={`/pipeline-templates/${a.integration_id}/routes/${a.route_id}/integration-code`}
                                                            title="Pipeline template — every property running it"
                                                        >
                                                            {a.template_name}{a.route_name ? ` · ${a.route_name}` : ''}
                                                        </Link>
                                                    ))}
                                                    {(r.attachments || []).map(a => (
                                                        <Link
                                                            key={a.access_id}
                                                            className="uapi-cc-attachment"
                                                            to={`/universal-api/authentication/${a.hotel_id}/access/${a.access_id}`}
                                                            title={a.uapi_hotel_id}
                                                        >
                                                            {a.hotelname}{a.label ? ` · ${a.label}` : ''}
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                        <td>{formatDateTime(r.updated_at)}</td>
                                    </tr>
                                ))}
                                {shown.length === 0 && (
                                    <tr><td colSpan={7} className="lu-muted">Nothing matches “{filter}”.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </>
    );
};

export default UapiCustomCodeTab;
